import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthStatusScreen } from "@/components/auth/AuthStatusScreen";
import { useAuth } from "@/contexts/AuthContext";

// One-time SSO codes are consumed on the backend; React 18 StrictMode
// mounts effects twice in dev, which would cause the second mount to
// re-POST the same code and get a 401. Share the in-flight promise
// across mounts so only one backend call happens and both effects
// observe the same outcome.
const ssoExchangeByCode = new Map<string, Promise<void>>();

// Mirrors backend `sanitizeReturnTo` — any path the SSO flow is allowed
// to land the user on after exchange. Belt-and-braces: backend already
// validates before embedding, this stops a hand-edited URL bar.
const ALLOWED_SSO_RETURN_TO_PREFIXES = ["/accept-invitation"];

function safeReturnTo(value: string | null): string {
  if (!value || !value.startsWith("/")) return "/";
  const allowed = ALLOWED_SSO_RETURN_TO_PREFIXES.some(
    (prefix) => value === prefix || value.startsWith(`${prefix}?`),
  );
  return allowed ? value : "/";
}

export function SsoCallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const error = searchParams.get("error");
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const { exchangeSsoCodeAndLogin } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<
    | "pending"
    | "success"
    | "error"
    | "usePassword"
    | "accountExists"
    | "emailNotVerified"
  >("pending");

  useEffect(() => {
    if (error === "use_password") {
      setStatus("usePassword");
      return;
    }
    if (error === "account_exists") {
      setStatus("accountExists");
      return;
    }
    if (error === "email_not_verified") {
      setStatus("emailNotVerified");
      return;
    }
    if (error) {
      toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
      setStatus("error");
      return;
    }
    if (!code) {
      toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
      setStatus("error");
      return;
    }
    let cancelled = false;
    let pending = ssoExchangeByCode.get(code);
    if (!pending) {
      pending = exchangeSsoCodeAndLogin(code);
      ssoExchangeByCode.set(code, pending);
    }
    pending
      .then(() => {
        if (!cancelled) setStatus("success");
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, error, exchangeSsoCodeAndLogin]);

  if (status === "success") {
    return <Navigate to={returnTo} replace />;
  }

  if (status === "usePassword") {
    return (
      <AuthStatusScreen
        state="info"
        title={t("auth.sso.usePasswordTitle", "Iniciá con contraseña")}
        message={t(
          "auth.sso.usePassword",
          "Para esta cuenta tenés que iniciar sesión con tu correo y contraseña.",
        )}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("auth.sso.usePasswordLink", "Ir al inicio de sesión")}
          </Link>
        }
      />
    );
  }

  if (status === "accountExists") {
    return (
      <AuthStatusScreen
        state="info"
        title={t("auth.sso.accountExistsTitle", "Ya tenés una cuenta")}
        message={t(
          "auth.sso.accountExists",
          "Ya existe una cuenta con este correo. Iniciá sesión con tu contraseña.",
        )}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("auth.sso.accountExistsLink", "Ir a iniciar sesión")}
          </Link>
        }
      />
    );
  }

  if (status === "emailNotVerified") {
    return (
      <AuthStatusScreen
        state="error"
        title={t(
          "auth.sso.emailNotVerifiedTitle",
          "Verificá tu correo en Google",
        )}
        message={t(
          "auth.sso.emailNotVerified",
          "Tu correo de Google no está verificado. Verificalo y volvé a intentar.",
        )}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-ghost">
            {t("pages.common.back", "Volver")}
          </Link>
        }
      />
    );
  }

  if (status === "error") {
    return (
      <AuthStatusScreen
        state="error"
        title={t("auth.sso.errorTitle", "No pudimos iniciarte sesión")}
        message={t("auth.sso.error", "Algo salió mal. Intentá de nuevo.")}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("pages.common.back", "Volver")}
          </Link>
        }
      />
    );
  }

  return (
    <AuthStatusScreen
      state="loading"
      title={t("auth.sso.completingTitle", "Completando inicio de sesión")}
      message={t(
        "auth.sso.completingDetail",
        "Estamos verificando tu cuenta con Google. Esto toma un segundo.",
      )}
    />
  );
}
